import React, { useRef, useState, useEffect, useCallback } from 'react'

import { prepCanvas } from './canvasUtil'

interface Props { title?: string; description?: string; onComplete?: () => void }

// Định luật Hooke (Lớp 10 — Bài 20): F = k·Δl (trong giới hạn đàn hồi).
// Chỉnh độ cứng k và độ giãn Δl → lực đàn hồi F; đồ thị F theo Δl là đường thẳng qua gốc.
const HookeSpringSimulation: React.FC<Props> = ({
  title = 'Định luật Hooke — F = k·Δl',
  description = 'Lực đàn hồi tỉ lệ với độ biến dạng. Đồ thị F–Δl là đường thẳng; độ dốc chính là độ cứng k.',
  onComplete,
}) => {
  const cv = useRef<HTMLCanvasElement>(null)
  const [k, setK] = useState(20)     // N/m
  const [dl, setDl] = useState(0.1)  // m
  const firstRef = useRef(false)
  const F = k * dl
  const W = 620, H = 280

  const draw = useCallback(() => {
    const ctx = prepCanvas(cv.current, W, H); if (!ctx) return
    ctx.clearRect(0, 0, W, H)
    // ── Lò xo (trái) ──
    const sx = 90, top = 30, natural = 90, stretch = dl * 600
    ctx.fillStyle = '#64748b'; ctx.fillRect(sx - 30, top - 8, 60, 8) // giá treo
    ctx.strokeStyle = '#94a3b8'; ctx.lineWidth = 2.5; ctx.beginPath()
    const len = natural + stretch, coils = 12
    for (let i = 0; i <= coils; i++) { const yy = top + (len) * (i / coils); const xx = sx + (i % 2 === 0 ? -14 : 14); i === 0 ? ctx.moveTo(sx, top) : ctx.lineTo(xx, yy) }
    ctx.lineTo(sx, top + len); ctx.stroke()
    // vật
    const my = top + len
    const g = ctx.createLinearGradient(0, my, 0, my + 44); g.addColorStop(0, '#fcd34d'); g.addColorStop(1, '#d97706')
    ctx.fillStyle = g; ctx.fillRect(sx - 26, my, 52, 44); ctx.strokeStyle = '#92400e'; ctx.strokeRect(sx - 26, my, 52, 44)
    // Δl chú thích
    ctx.strokeStyle = '#ef4444'; ctx.setLineDash([4, 3]); ctx.beginPath(); ctx.moveTo(sx + 50, top + natural); ctx.lineTo(sx + 50, my); ctx.stroke(); ctx.setLineDash([])
    ctx.fillStyle = '#dc2626'; ctx.font = 'bold 12px sans-serif'; ctx.fillText(`Δl=${(dl * 100).toFixed(0)}cm`, sx + 56, (top + natural + my) / 2)
    // Lực đàn hồi tác dụng lên vật: giãn (Δl>0) → hướng LÊN; nén (Δl<0) → hướng XUỐNG; độ lớn ∝ |F|
    if (Math.abs(F) > 0.05) {
      const ax = sx - 58, ay = my + 22, len = Math.min(150, Math.abs(F) * 5), dir = F > 0 ? -1 : 1
      const ey = ay + dir * len
      ctx.strokeStyle = '#16a34a'; ctx.fillStyle = '#16a34a'; ctx.lineWidth = 3
      ctx.beginPath(); ctx.moveTo(ax, ay); ctx.lineTo(ax, ey); ctx.stroke()
      ctx.beginPath(); ctx.moveTo(ax, ey); ctx.lineTo(ax - 5, ey - dir * 9); ctx.lineTo(ax + 5, ey - dir * 9); ctx.closePath(); ctx.fill()
      ctx.font = 'bold 12px sans-serif'; ctx.fillText('Fđh', ax - 22, dir < 0 ? ey - 4 : ey + 14)
    }

    // ── Đồ thị F–Δl (phải) — gồm cả miền NÉN (Δl<0, F<0) ──
    const gx0 = 305, gx1 = W - 16, gyTop = 16, gyBot = H - 22
    const DLMIN = -0.1, DLMAX = 0.3, FMINg = -5, FMAXg = 15
    const GX = (d: number) => gx0 + ((d - DLMIN) / (DLMAX - DLMIN)) * (gx1 - gx0)
    const GY = (f: number) => gyBot - ((f - FMINg) / (FMAXg - FMINg)) * (gyBot - gyTop)
    const ox = GX(0), oy = GY(0)
    ctx.strokeStyle = '#eef2f7'; ctx.lineWidth = 1
    for (let d = -0.1; d <= 0.3001; d += 0.1) { ctx.beginPath(); ctx.moveTo(GX(d), gyTop); ctx.lineTo(GX(d), gyBot); ctx.stroke() }
    // trục qua gốc + mũi tên
    ctx.strokeStyle = '#94a3b8'; ctx.fillStyle = '#94a3b8'; ctx.lineWidth = 1.5
    ctx.beginPath(); ctx.moveTo(ox, gyTop); ctx.lineTo(ox, gyBot); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(gx0, oy); ctx.lineTo(gx1, oy); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(ox, gyTop); ctx.lineTo(ox - 4, gyTop + 8); ctx.lineTo(ox + 4, gyTop + 8); ctx.closePath(); ctx.fill()
    ctx.beginPath(); ctx.moveTo(gx1, oy); ctx.lineTo(gx1 - 8, oy - 4); ctx.lineTo(gx1 - 8, oy + 4); ctx.closePath(); ctx.fill()
    ctx.fillStyle = '#475569'; ctx.font = 'bold 12px sans-serif'; ctx.fillText('F(N)', ox + 5, gyTop + 10); ctx.fillText('Δl(m)', gx1 - 38, oy - 6)
    // đường thẳng F = k·Δl qua gốc (cả 2 phía)
    const fLo = Math.max(FMINg, Math.min(FMAXg, k * DLMIN)), fHi = Math.max(FMINg, Math.min(FMAXg, k * DLMAX))
    ctx.strokeStyle = '#2563eb'; ctx.lineWidth = 3.5; ctx.beginPath(); ctx.moveTo(GX(DLMIN), GY(fLo)); ctx.lineTo(GX(DLMAX), GY(fHi)); ctx.stroke()
    // điểm hiện tại
    const px = GX(Math.max(DLMIN, Math.min(DLMAX, dl))), py = GY(Math.max(FMINg, Math.min(FMAXg, F)))
    ctx.strokeStyle = 'rgba(239,68,68,0.5)'; ctx.setLineDash([3, 3]); ctx.beginPath(); ctx.moveTo(px, oy); ctx.lineTo(px, py); ctx.moveTo(ox, py); ctx.lineTo(px, py); ctx.stroke(); ctx.setLineDash([])
    ctx.fillStyle = '#ef4444'; ctx.beginPath(); ctx.arc(px, py, 6, 0, Math.PI * 2); ctx.fill(); ctx.strokeStyle = '#fff'; ctx.lineWidth = 2; ctx.stroke()
  }, [k, dl])
  useEffect(() => { draw() }, [draw])

  // ── Kéo vật lên/xuống bằng tay để đổi độ giãn Δl ──
  const dragRef = useRef(false)
  const onDown = (e: React.PointerEvent) => {
    const r = cv.current!.getBoundingClientRect()
    const mx = (e.clientX - r.left) / r.width * W, my = (e.clientY - r.top) / r.height * H
    const massY = 120 + dl * 600 + 22          // tâm vật (top=30, natural=90)
    if (Math.abs(mx - 90) < 40 && Math.abs(my - massY) < 36) {
      dragRef.current = true; firstRef.current || (firstRef.current = true, onComplete?.()); (e.target as HTMLElement).setPointerCapture?.(e.pointerId)
    }
  }
  const onMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return
    const r = cv.current!.getBoundingClientRect()
    const my = (e.clientY - r.top) / r.height * H
    setDl(Math.max(-0.1, Math.min(0.3, (my - 22 - 120) / 600)))
  }
  const onUp = () => { dragRef.current = false }

  return (
    <div className="rounded-2xl border border-amber-200 bg-white p-4 my-3">
      <h4 className="font-bold text-amber-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <canvas ref={cv} width={W} height={H}
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp}
        className="w-full rounded-lg border border-gray-100 bg-slate-50 touch-none cursor-grab active:cursor-grabbing" />
      <div className="text-center p-2 rounded-xl bg-amber-50 my-3"><span className="font-black text-amber-700 text-lg">F = {F.toFixed(1)} N</span> <span className="text-xs text-gray-500">= k·Δl = {k} × {dl.toFixed(2)}</span></div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <label className="flex items-center gap-2"><span className="w-24 text-gray-600">Độ cứng k</span><input type="range" min={5} max={50} step={5} value={k} onChange={(e) => { firstRef.current || (firstRef.current = true, onComplete?.()); setK(Number(e.target.value)) }} className="flex-1" /><span className="w-16 text-right font-semibold">{k} N/m</span></label>
        <label className="flex items-center gap-2"><span className="w-24 text-gray-600">Độ biến dạng Δl</span><input type="range" min={-0.1} max={0.3} step={0.01} value={dl} onChange={(e) => { firstRef.current || (firstRef.current = true, onComplete?.()); setDl(Number(e.target.value)) }} className="flex-1" /><span className="w-16 text-right font-semibold">{(dl * 100).toFixed(0)} cm</span></label>
      </div>
    </div>
  )
}
export default HookeSpringSimulation
